import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Speed,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

interface PerformanceMetrics {
  fps: number;
  memory: number;
  cpu: number;
  renderTime: number;
}

const PerformanceMonitor: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    memory: 0,
    cpu: 0,
    renderTime: 0,
  });

  useEffect(() => {
    if (!open) return;

    let frameCount = 0;
    let lastTime = performance.now();
    let animationFrameId: number;

    const updateMetrics = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        frameCount = 0;
        lastTime = currentTime;

        // Get memory usage if available
        const memory = (performance as any).memory
          ? Math.round((performance as any).memory.usedJSHeapSize / 1048576)
          : 0;

        setMetrics((prev) => ({
          ...prev,
          fps,
          memory,
        }));
      }

      animationFrameId = requestAnimationFrame(updateMetrics);
    };

    animationFrameId = requestAnimationFrame(updateMetrics);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [open]);

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        minWidth: 200,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          cursor: 'pointer',
        }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Speed fontSize="small" />
          <Typography variant="body2">Performance</Typography>
          <Chip label={`${metrics.fps} FPS`} size="small" color="primary" />
        </Box>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">FPS</Typography>
              <Typography variant="caption">{metrics.fps}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((metrics.fps / 60) * 100, 100)}
              color={metrics.fps < 30 ? 'error' : metrics.fps < 50 ? 'warning' : 'success'}
            />
          </Box>
          {metrics.memory > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Memory</Typography>
                <Typography variant="caption">{metrics.memory} MB</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((metrics.memory / 100) * 100, 100)}
                color={metrics.memory > 80 ? 'error' : metrics.memory > 60 ? 'warning' : 'success'}
              />
            </Box>
          )}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Render Time</Typography>
              <Typography variant="caption">{metrics.renderTime.toFixed(2)} ms</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((metrics.renderTime / 16) * 100, 100)}
              color={metrics.renderTime > 16 ? 'error' : metrics.renderTime > 10 ? 'warning' : 'success'}
            />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PerformanceMonitor;

